import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import { IAttachment, IAttachmentTypes, IInsertAttachment, IInsertAttachmentResponse } from '../models/attachment-types.model';
import { ValidateAndDefault } from '../models/validate-and-default.model';
import { IInsertUpdateData } from '../models/insert-update-data.model';
import { FormItemField, FormItemGroup } from '../models/form-item.model';
import { ModalFormMetadata } from '../models/modal-form-metadata.model';
import { GetFormDataParams, IOfficeData, OfficeFieldsValuesObj } from '../models/office-data.model';
import { IOfficeFormDetails, IOfficeGetFormDetailsParams } from '../models/office-form-details.model';
import { FormEventsModalConfig } from '../office-form/events/models/form-events-modal-config.model';
import { TabsDataResultModel } from '@pages/office/models/tabs-data-result.model';
import { ApiDataResponse } from '@shared/model/api-response.model';
import { IDeleteResultModel } from '@pages/office/models/delete-result.model';
import { IOfficeCompany } from '../models/companies.model';
import { IOfficeFormListItem } from '../models/form-list.models';
import { ISaveExcelColumnParam, ISaveExcelFilterParam, ISaveExcelOrderByParam } from '../models/save-excel-file.model';
import { IUploadHQCompanyLogoRequest } from '../models/hq-company-logo.model';

@Injectable({
  providedIn: 'root',
})
export class OfficeService {
  private http = inject(HttpClient);
  private router = inject(Router);
  protected route = inject(ActivatedRoute);

  public getAttachmentList(objectParams: { co: number; form: string; keyID: number; UniqueAttchID: string }): Observable<IAttachment[]> {
    const params = new HttpParams({ fromObject: objectParams });
    return this.http.get<IAttachment[]>('/Office/GetAttachmentList', { params });
  }

  public insertAttachment(attachmentData: IInsertAttachment): Observable<IInsertAttachmentResponse> {
    return this.http.post<IInsertAttachmentResponse>('/Office/InsertAttachment', attachmentData);
  }

  public updateAttachmentMetadata(
    co: number,
    form: string,
    keyID: number,
    description: string,
    attachmentID: number,
    attachmentTypeID: number
  ): Observable<never> {
    return this.http.post<never>('/Office/UpdateAttachmentMetadata', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
      description: description,
      attachmentTypeID: attachmentTypeID,
    });
  }

  public deleteAttachment(co: number, form: string, keyID: number, attachmentID: number): Observable<never> {
    return this.http.post<never>('/Office/DeleteAttachment', {
      co: co,
      form: form,
      keyID: keyID,
      attachmentID: attachmentID,
    });
  }

  public getAttachmentTypes(co: number): Observable<IAttachmentTypes[]> {
    const params = new HttpParams().append('co', co);
    return this.http.get<IAttachmentTypes[]>('/Office/AttachmentTypes', { params });
  }
  /**
   * @returns {IOfficeCompany[]}
   */
  public getCompanies(): Observable<IOfficeCompany[]> {
    return this.http.get<IOfficeCompany[]>('/Office/GetCompanies');
  }
  /**
   * @param {company} number
   * @param {module} string
   * @returns {IOfficeFormListItem[]}
   */
  public getFormList(company: number, module: string): Observable<IOfficeFormListItem[]> {
    return this.http.get<IOfficeFormListItem[]>('/Office/GetForms?co=' + company + '&module=' + module);
  }

  getFormMetadata(tableName: any, company: any) {
    return this.http.get('/Office/GetFormMetadata?form=' + tableName + '&co=' + company).toPromise();
  }

  /**
   * @param {string} tableName
   * @param {number} company
   * @param {string[]} orderby
   * @param {number} keyid
   * @param {number} rowsPerPage
   * @param {number} pageNumber
   * @param {string[]} search
   * @param {string[]} filter
   * @param {string} parentForm
   * @param {parentFormData} parentForm
   * @param {includeMetadata} boolean
   * @returns {IOfficeData} - Observable
   */
  public getFormData(params: GetFormDataParams): Observable<IOfficeData> {
    return this.http.post<IOfficeData>('/Office/GetData', params);
  }
  /**
   * @param {number} company
   * @param {string} form
   * @param {string} formData
   * @returns {IOfficeFormDetails} - Observable
   */
  public getFormDetails(params: IOfficeGetFormDetailsParams): Observable<IOfficeFormDetails> {
    return this.http.post<IOfficeFormDetails>('/Office/getFormDetails', params);
  }
  /**
   * @param {number} currentCompany
   * @param {string} currentForm
   * @param {string} postData stringify object
   * @param {number} keyID
   * @param {string[]} dirtyFields
   * @param {string[]} disabledAndHiddenFields
   * @returns {IInsertUpdateData} - Observable
   */
  public insertUpdateData(
    co: number,
    form: string,
    formData: string,
    keyID: number,
    dirtyFields: string[],
    disabledAndHiddenFields: string[]
  ): Observable<IInsertUpdateData> {
    return this.http.post<IInsertUpdateData>('/Office/InsertUpdateData', {
      co: co,
      form: form,
      formData: formData,
      keyID: keyID,
      dirtyFields: dirtyFields,
      disabledAndHiddenFields: disabledAndHiddenFields,
    });
  }
  /**
   * @param {FormEventsModalConfig} paramsObj
   * @returns {ModalFormMetadata} - Observable
   */
  public getModalMetadata(paramsObj: FormEventsModalConfig): Observable<ModalFormMetadata> {
    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<ModalFormMetadata>('/Office/ModalForm', { params });
  }
  /**
   * @param {FormEventsModalConfig} paramsObj
   * @param {OfficeFieldsValuesObj} modalFormData
   * @param {string} nextStep
   * @returns {Observable<ModalFormMetadata>}
   */
  public postModalForm(
    paramsObj: FormEventsModalConfig,
    confirmed: boolean,
    modalFormData: OfficeFieldsValuesObj,
    nextStep: string
  ): Observable<ModalFormMetadata> {
    return this.http.post<ModalFormMetadata>('/Office/ModalForm', {
      ...paramsObj,
      confirmed,
      modalFormData,
      nextStep,
    });
  }

  /**
   * Send delete request for given form
   * @param co Company id
   * @param form Form for removal
   * @param keyID Record identification
   */
  deleteRow(co: number, form: string, keyID: number): Observable<ApiDataResponse<IDeleteResultModel>> {
    return this.http.post<ApiDataResponse<IDeleteResultModel>>('/Office/DeleteData', {
      co: co,
      form: form,
      keyID: keyID,
    });
  }

  /**
   * Get internal tabs data of given form
   * @param co Company id
   * @param form CurrentForm
   * @param keyID Record identification or -1 for create
   */
  getTabsData(co: number, form: string, keyID: number): Observable<TabsDataResultModel> {
    const params = new HttpParams().appendAll({ co: co, form: form, keyID: keyID });
    return this.http.get<TabsDataResultModel>('/Office/GetTabsData', { params });
  }

  getAttachmentData(co: any, form: any, keyID: any, attachmentID: any) {
    return this.http
      .get('/Office/GetAttachmentData?co=' + co + '&form=' + form + '&keyID=' + keyID + '&attachmentID=' + attachmentID)
      .toPromise();
  }
  /**
   * @param {string} currentForm
   * @returns {FormItemGroup[]} - Observable
   */
  public getFormItemGroups(form: string): Observable<FormItemGroup[]> {
    const params = new HttpParams().set('form', form);
    return this.http.get<FormItemGroup[]>('/Office/GetFormItemGroups', { params });
  }
  /**
   * @param {string} currentForm
   * @returns {FormItemField[]} - Observable
   */
  public getFormItemFields(form: string): Observable<FormItemField[]> {
    const params = new HttpParams().set('form', form);
    return this.http.get<FormItemField[]>('/Office/GetFormItemFields', { params });
  }

  getFormReports(co: any, form: any) {
    return this.http.get('/Office/getFormReports?co=' + co + '&form=' + form).toPromise();
  }

  getLookup(co: any, form: any, seq: any, lookup: any, formData: any, searchString: any) {
    return this.http
      .post('/Office/getLookup', {
        co: co,
        form: form,
        seq: seq,
        lookup: lookup,
        formData: formData,
        searchString: searchString,
      })
      .toPromise();
  }

  getStaticLookups(co: any, form: any) {
    return this.http.get('/Office/getStaticLookups?co=' + co + '&form=' + form).toPromise();
  }

  getVaInquiries(co) {
    return this.http.get('/Office/GetVaInquiries?co=' + co).toPromise();
  }

  getVaInquiryData(co, queryName, paramValues) {
    const url = '/Office/GetVaInquiryData?co=' + co + '&queryName=' + queryName;
    let params = '';
    for (let i = 0; i < paramValues.length; i++) {
      params += '&paramValues[' + i + ']=' + encodeURIComponent(paramValues[i]);
    }
    return this.http.get(url + params).toPromise();
  }

  getVaInquiryLookup(co, lookup, dependentData = null) {
    let url = '/Office/GetVaInquiryLookup?co=' + co + '&lookup=' + lookup;
    if (dependentData != null) url += '&dependentData=' + encodeURIComponent(dependentData);
    return this.http.get(url).toPromise();
  }

  getVaInquiryParameters(queryName) {
    return this.http.get('/Office/GetVaInquiryParameters?queryName=' + queryName).toPromise();
  }
  /**
   * @param {string} currentForm
   * @param {FormItemGroup[]} groups
   * @param {FormItemField[]} fields
   * @returns {never} - Observable
   */
  public updateFormState(form, groups, fields): Observable<never> {
    const url = '/Office/UpdateFormState';
    const postData = {
      form: form,
      groups: groups,
      fields: fields,
    };
    return this.http.post<never>(url, postData);
  }
  /**
   * @param {form} currentForm
   * @param {co} currentCompany
   * @param {orderby} sort settings
   * @param {where} filter settings
   * @param {columns} grid columns
   * @param {fileName} fileName
   * @returns {boolean} - Observable
   */
  public getExcelFile(
    form: string,
    co: number,
    orderby: ISaveExcelOrderByParam[],
    where: ISaveExcelFilterParam[],
    columns: ISaveExcelColumnParam[],
    fileName: string
  ): Observable<boolean> {
    return this.http
      .post('/office/SaveExcelFile', {
        form,
        co,
        orderby,
        where,
        columns,
      })
      .pipe(
        map(response => {
          if (response['success'] == false) {
            return false;
          } else {
            const url = '/Office/GetExcelFile?formName=' + form + '&fileName=' + (fileName ?? form);
            window.location.href = url;
            return true;
          }
        })
      );
  }

  formTrack(formName) {
    if (formName == '1' || formName == null) return;

    const url = '/Office/FormTrack';
    const postData = {
      formName: formName,
    };
    return this.http.post(url, postData).toPromise();
  }

  /**
   * @param {number} currentCompany
   * @param {string} currentForm
   * @param {number} seq - validating field seq
   * @param {string} formData - JSON stringify object values
   * @param {string} keyID - Key of object or -1 for insert
   * @returns {ValidateAndDefault} - Observable
   */
  public validateAndDefault(co: number, form: string, seq: number, formData: string, keyID: number): Observable<ValidateAndDefault> {
    return this.http.post<ValidateAndDefault>('/Office/ValidateAndDefault', {
      co: co,
      form: form,
      seq: seq,
      keyID: keyID,
      formData: formData,
    });
  }

  /**
   * Retrieves the headquarters company logo as a Blob image.
   *
   * @param {number} companyId - The unique identifier of the company.
   * @returns {Observable<Blob>} Observable emitting the logo image as a Blob.
   */
  public getHQCompanyLogo(companyId: number): Observable<Blob> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http.get('/Office/GetHQCompanyLogo', { responseType: 'blob', params });
  }

  /**
   * Uploads a new headquarters company logo.
   *
   * @param {IUploadHQCompanyLogoRequest} request - Object containing companyId, image (file data), and originalFileName.
   * @returns {Observable<ApiDataResponse<void>>} Observable emitting the upload result (success or error).
   */
  public uploadHQCompanyLogo(request: IUploadHQCompanyLogoRequest): Observable<ApiDataResponse<void>> {
    return this.http.post<ApiDataResponse<void>>('/Office/UploadHQCompanyLogo', {
      companyId: request.companyId,
      image: request.image,
      originalFileName: request.originalFileName,
      menuImage: request.menuImage,
    });
  }

  /**
   * Deletes the headquarters company logo for the specified company.
   *
   * @param {number} companyId - The unique identifier of the company.
   * @returns {Observable<ApiDataResponse<void>>} Observable emitting the deletion result (success or error).
   */
  public deleteHQCompanyLogo(companyId: number): Observable<ApiDataResponse<void>> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http.delete<ApiDataResponse<void>>('/Office/DeleteHQCompanyLogo', { params });
  }
}
